import type ReportingMathsSummaryTableRoute from 'teacher-ui-client/reporting/ui-scope/maths/summary/table/route'
import { t } from 'ember-intl'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { not, eq } from 'ember-truth-helpers'
import IsSubco from 'teacher-ui-client/helpers/is-subco'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { SummaryTablesTeachers } from 'teacher-ui-client/components/summary-tables/teachers'
import { SummaryTablesStudents } from 'teacher-ui-client/components/summary-tables/students'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import type Owner from '@ember/owner'

interface Signature {
  Args: {
    model: ModelFor<ReportingMathsSummaryTableRoute>
  }
}

type SummaryTab = 'teachers' | 'students'

export class ReportingUiScopeMathsSummaryTablePage extends Component<Signature> {
  @tracked currentTab!: SummaryTab

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)

    this.currentTab = args.model.isSubco ? 'teachers' : 'students'
  }

  get isTeacherTab() {
    return this.currentTab === 'teachers'
  }

  setCurrentTab = (tab: SummaryTab) => {
    this.currentTab = tab
  }

  <template>
    <ReportingTeacherUiHeader @title={{t "reporting.summaryReportTitles.heading"}} />
    <ReportingDetailedControls
      @students={{@model.students}}
      @exportOptions={{array ExportType.CSV ExportType.print}}
      @isDataPresent={{true}}
      @showScopePicker={{not this.isTeacherTab}}
      @showDatePicker={{true}}
      @studentRouteName="reporting.ui-scope.maths.student-summary"
    />
    <div>
      {{#if (IsSubco)}}
        <TabGroupComponent
          @theme={{themeKey "maths"}}
          @tabType={{TAB_TYPE_KEYS.shadow}}
          data-test-tab-group
          as |tabGroup|
        >
          <tabGroup.tab
            data-test-tab-all-teachers
            @isActive={{eq this.currentTab "teachers"}}
            {{on "click" (fn this.setCurrentTab "teachers")}}
          >
            {{t "reporting.summaryReportTitles.teachersTab"}}
          </tabGroup.tab>
          <tabGroup.tab
            data-test-tab-my-students
            @isActive={{eq this.currentTab "students"}}
            {{on "click" (fn this.setCurrentTab "students")}}
          >
            {{t "reporting.summaryReportTitles.studentsTab"}}
          </tabGroup.tab>
        </TabGroupComponent>
      {{/if}}

      <ContentTile class="relative print:border-none {{if (IsSubco) 'rounded-tl-none'}}">
        {{#if this.isTeacherTab}}
          <SummaryTablesTeachers
            @product={{@model.product}}
            @teachers={{@model.mathsTeachers}}
            @teachersData={{@model.schoolSimpleSummary.teachers}}
            @schoolData={{@model.schoolSimpleSummary.school}}
          />
        {{else}}
          <SummaryTablesStudents
            @product={{@model.product}}
            @students={{@model.students}}
            @studentsData={{@model.studentsSimpleSummary.students}}
            @totalsData={{@model.studentsSimpleSummary.totals}}
          />
        {{/if}}
      </ContentTile>
    </div>
  </template>
}

export default RouteTemplate(ReportingUiScopeMathsSummaryTablePage)
